/* eslint-disable import/no-duplicates */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { differenceInHours, format, formatDistanceStrict, formatDuration } from 'date-fns';
import { enUS, ja, ko, th, zhTW } from 'date-fns/locale';
import { useCallback } from 'react';
import * as yup from 'yup';
import { HttpError } from '~/components/Error/HttpError';
import { LanguageKey, getResolvedLanguage } from '~/components/I18n';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useSearchParams } from '~/components/Route';
import { sharedDateTimer, useCountdown, useNow } from '~/components/Util';

export type MissionItem = {
  name: string;
  owned: boolean;
  thumbnailUrl: string;
  contentsUrl: string;
  marketplaceUrl: string;
};

export type Mission = {
  id: number;
  rewardAssetType: 'DON' | 'LUP';
  title: string;
  rewardAmount: number;
  startAt: Date;
  endAt: Date;
  status: 'upcoming' | 'in_progress' | 'ended';
  missionCompleted: boolean;
  missionItemList: MissionItem[];
};

const localeMap: Record<LanguageKey, Locale> = {
  en: enUS,
  ko,
  ja,
  zh_TW: zhTW,
  th,
};

export const useRemainingTime = (date: Date) => {
  const now = useNow();
  const diff = differenceInHours(date, now);
  const countdown = useCountdown(date);
  const locale = localeMap[getResolvedLanguage()];

  if (diff > 0) {
    return formatDistanceStrict(date, now, {
      roundingMethod: 'floor',
      locale,
    });
  } else if (countdown.total > 0) {
    return formatDuration(countdown, {
      format: [countdown.minutes > 0 ? 'minutes' : 'seconds'],
      zero: true,
      locale,
    });
  } else {
    return format(date, 'PP p');
  }
};

export type Status = 'all' | 'partial' | 'complete' | 'end';

export const schema = yup.object().shape({
  status: yup.string().oneOf<Status>(['all', 'partial', 'complete', 'end']).default('all'),
});
export const useMissionPageParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<{ status: Status }>({ converter });
};

export const missionUrl = '/api/citizen/v1/mission';

export const transformMission = (mission: Mission): Mission => {
  const startAt = new Date(mission.startAt);
  const endAt = new Date(mission.endAt);
  const currentDate = sharedDateTimer.now;
  const status = (() => {
    if (endAt < currentDate) return 'ended';
    if (startAt > currentDate) return 'upcoming';
    return 'in_progress';
  })();

  return {
    ...mission,
    startAt,
    endAt,
    status,
  };
};

export const getMissions = async () => {
  const { data } = await axios.get<Mission[]>(missionUrl);
  return data.map(transformMission);
};

export const useMissions = <T = Mission[]>(
  options?: UseDosiminQueryOptions<Mission[], HttpError, T>
) =>
  useQuery<Mission[], HttpError, T>({
    queryKey: ['mission'],
    queryFn: getMissions,
    initialData: [],
    ...options,
  });

export const selectMissionGroup = (data: Mission[]): Record<Status, Mission[]> => {
  const complete: Mission[] = [];
  const partial: Mission[] = [];
  const end: Mission[] = [];

  data.forEach((each) => {
    const hasOwnedOneNft = each.missionItemList.some((missionItem) => missionItem.owned);

    if (each.status === 'ended') {
      end.push(each);
      return;
    }
    if (each.missionCompleted) {
      complete.push(each);
      return;
    }
    if (hasOwnedOneNft) {
      partial.push(each);
    }
  });

  return { all: data, complete, partial, end };
};

export const useMissionGroup = (
  options?: Omit<UseDosiminQueryOptions<Mission[], HttpError, Record<Status, Mission[]>>, 'select'>
) =>
  useMissions({
    select: selectMissionGroup,
    ...options,
  });
